/**
 * DataSet Manager
 * Manages loading, caching, and retrieval of DataSet data for DROPDOWN_DB fields.
 *
 * Configuration is read from existing columns:
 * - _uid: DataSet name (unique identifier)
 * - Value: JSON string with {"SourceType":"LOCAL","Data":[...]} or {"SourceType":"API","Url":"..."}
 *
 * Supports two source types:
 * - LOCAL: Data is embedded in Value.Data as an array
 * - API: Data is fetched from URL in Value.Url
 */

// In-memory cache for DataSets
// Structure: { [name]: { config, data, timestamp } }
const dataSetCache = {};

// Cache TTL in milliseconds (default: 5 minutes)
const CACHE_TTL = 5 * 60 * 1000;

/**
 * Check if cached data is still valid
 * @param {string} name - DataSet name
 * @returns {boolean}
 */
function isCacheValid(name) {
  const cached = dataSetCache[name];
  if (!cached) return false;

  const now = Date.now();
  return (now - cached.timestamp) < CACHE_TTL;
}

/**
 * Parse DataSet configuration from field
 * Reads from existing columns: _uid (name) and Value (JSON config)
 * @param {Object} field - Field from form definition
 * @returns {Object|null} - Parsed config or null if invalid
 */
export function parseDataSetConfig(field) {
  if (!field || field.Type !== 'DataSet') {
    return null;
  }

  const name = field._uid;
  if (!name) {
    console.error('DataSetManager: DataSet field missing _uid');
    return null;
  }

  // Parse Value column as JSON
  let valueConfig = {};
  if (field.Value) {
    try {
      if (typeof field.Value === 'string') {
        valueConfig = JSON.parse(field.Value);
      } else if (typeof field.Value === 'object') {
        valueConfig = field.Value;
      }
    } catch (e) {
      console.error(`DataSetManager: Failed to parse Value JSON for "${name}":`, e);
      return null;
    }
  }

  return {
    Name: name,
    SourceType: valueConfig.SourceType || 'LOCAL',
    Data: valueConfig.Data || [],
    Url: valueConfig.Url || '',
    originalField: field
  };
}

/**
 * Load a single DataSet from its configuration
 * @param {Object} field - DataSet field from form definition
 * @returns {Promise<Array>} - The loaded data array
 */
export async function loadDataSet(field) {
  const config = parseDataSetConfig(field);
  if (!config) {
    return [];
  }

  const { Name, SourceType, Data, Url } = config;

  // Check cache first
  if (isCacheValid(Name)) {
    return dataSetCache[Name].data;
  }

  let data = [];

  try {
    if (SourceType === 'LOCAL') {
      // LOCAL: Data is in the config
      if (Array.isArray(Data)) {
        data = Data;
      } else {
        console.error(`DataSetManager: LOCAL DataSet "${Name}" Data must be an array`);
        data = [];
      }
    } else if (SourceType === 'API') {
      // API: Fetch from URL via server proxy (to avoid CORS issues)
      if (!Url) {
        console.error(`DataSetManager: API DataSet "${Name}" requires Url in Value`);
        return [];
      }

      // Use server proxy to fetch external API data
      // Note: REACT_APP_SERVER_URL already includes /api
      const proxyUrl = `${process.env.REACT_APP_SERVER_URL}/DataSetProxy?url=${encodeURIComponent(Url)}`;

      const response = await fetch(proxyUrl, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const responseData = await response.json();

      // Handle different response formats
      if (Array.isArray(responseData)) {
        data = responseData;
      } else if (responseData && Array.isArray(responseData.data)) {
        data = responseData.data;
      } else {
        console.error(`DataSetManager: API response for "${Name}" is not an array`);
        data = [];
      }
    } else {
      console.error(`DataSetManager: Unknown SourceType "${SourceType}" for DataSet "${Name}"`);
      return [];
    }

    // Store in cache
    dataSetCache[Name] = {
      config,
      data,
      timestamp: Date.now(),
    };

    return data;
  } catch (error) {
    console.error(`DataSetManager: Error loading DataSet "${Name}":`, error);
    return [];
  }
}

/**
 * Load all DataSets from form fields
 * Scans fields for Type="DataSet" and loads them
 * @param {Array} fields - Array of field configurations
 * @returns {Promise<Object>} - Object with DataSet names as keys and data arrays as values
 */
export async function loadAllDataSets(fields) {
  if (!Array.isArray(fields)) {
    console.error('DataSetManager: loadAllDataSets requires an array of fields');
    return {};
  }

  // Find all DataSet fields
  const dataSetFields = fields.filter(field => field.Type === 'DataSet');

  // Load all DataSets in parallel
  const loadPromises = dataSetFields.map(field => loadDataSet(field));
  await Promise.all(loadPromises);

  // Return object with all loaded DataSets
  const result = {};
  dataSetFields.forEach(field => {
    const name = field._uid;
    if (name && dataSetCache[name]) {
      result[name] = dataSetCache[name].data;
    }
  });

  return result;
}

/**
 * Get cached DataSet configuration by name
 * @param {string} name - DataSet name
 * @returns {Object|null} - DataSet configuration or null if not found
 */
export function getByName(name) {
  const cached = dataSetCache[name];
  return cached ? cached.config : null;
}

/**
 * Get data array from cached DataSet
 * @param {string} name - DataSet name
 * @returns {Array} - Data array or empty array if not found
 */
export function getData(name) {
  const cached = dataSetCache[name];
  return cached ? cached.data : [];
}

/**
 * Clear all cached DataSets
 */
export function clearCache() {
  Object.keys(dataSetCache).forEach(key => {
    delete dataSetCache[key];
  });
}

/**
 * Clear specific DataSet from cache
 * @param {string} name - DataSet name to clear
 */
export function clearByName(name) {
  if (dataSetCache[name]) {
    delete dataSetCache[name];
  }
}

/**
 * Check if a DataSet is loaded in cache
 * @param {string} name - DataSet name
 * @returns {boolean}
 */
export function isLoaded(name) {
  return !!dataSetCache[name];
}

/**
 * Get all loaded DataSet names
 * @returns {Array<string>}
 */
export function getLoadedNames() {
  return Object.keys(dataSetCache);
}

// Default export as object for easier importing
const DataSetManager = {
  parseDataSetConfig,
  loadDataSet,
  loadAllDataSets,
  getByName,
  getData,
  clearCache,
  clearByName,
  isLoaded,
  getLoadedNames,
};

export default DataSetManager;
